
import { simRun, fault, simBuild, sendDesign, connect, SidecarFailure, failureOfPayload } from "./SidecarClient.js";
import { concat, replace, trimEnd } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { PromiseBuilder__Delay_62FBFDE1, PromiseBuilder__Run_212F1D4B } from "../fable_modules/Fable.Promise.3.1.3/Promise.fs.js";
import { promise } from "../fable_modules/Fable.Promise.3.1.3/PromiseImpl.fs.js";
import { map } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { Convert_serialize } from "../Common/SimpleJson/Json.Converter.js";
import { createTypeInfo } from "../Common/SimpleJson/TypeInfo.Converter.js";
import { SimpleSheet_$reflection } from "../Common/CommonTypes.js";
import { FSharpResult$2 } from "../fable_modules/fable-library-js.5.13.0/Result.js";

export const Constants_runChunkMs = 1000;

/**
 * The failure a sidecar reply carries, or None when it is not one. Every reply that can fail
 * answers with a JSON object whose "error" is the message written for the user (a refused build
 * says exactly what to set the cycle count to) and whose "kind" says whether the sidecar broke
 * or declined - so the envelope never reaches a screen, and the kind decides the severity.
 * 
 * The decoding itself is `SidecarClient.failureOfPayload`, which every other reader of an error
 * reply also goes through: one decoder, so a change to the envelope cannot leave half the
 * readers behind.
 */
export function errorIn(reply) {
    if (reply.startsWith("{\"error\"")) {
        const failure = failureOfPayload(reply);
        return new SidecarFailure(failure.Kind, trimEnd(replace(failure.Message, "simulation build failed: GenericSimError   \'", ""), "\'"));
    }
    else {
        return undefined;
    }
}

/**
 * Build the design on the sidecar, and answer with the session epoch that build issued.
 * 
 * **Unconditional.** Whether a build is needed at all is a question about what the sidecar is
 * believed to hold, which is `Model.SidecarBuild`, and it is answered before this is called - by
 * the update function, synchronously, from the model. This module used to hold that belief and
 * decide for itself, which put a fact the UI has to draw somewhere the UI could not read.
 * 
 * **One caller at a time, by construction rather than by guarding.** Issie runs one simulation:
 * starting the waveform simulator ends the step simulator and starting the step simulator ends
 * the waveform one (Update.endWaveSimulation), and a build is now a message, so the model is in
 * `SidecarBuilding` from the moment one starts until it answers - which is what stops a second
 * being started while the first is in flight.
 * 
 * It matters that this stays true: a design is uploaded one sheet per message and index 0 begins
 * an upload, discarding any abandoned one, so two builds interleaving leave the sidecar holding
 * half of each. That is reported rather than silent - the build fails with "no sheet called X in
 * the design" - but it is a simulation the user asked for and did not get.
 */
export function build(design, arraySize) {
    return PromiseBuilder__Run_212F1D4B(promise, PromiseBuilder__Delay_62FBFDE1(promise, () => (connect().then(() => {
        const sheetJsons = map((value) => Convert_serialize(value, createTypeInfo(SimpleSheet_$reflection())), design.Sheets);
        return sendDesign(design.TopSheet, sheetJsons).then((_arg_1) => {
            const matchValue = errorIn(_arg_1);
            if (matchValue == null) {
                return simBuild(arraySize).then((_arg_2) => {
                    const reply = _arg_2;
                    const matchValue_1 = errorIn(reply);
                    if (matchValue_1 == null) {
                        const epoch = ((function (t) { try { return JSON.parse(t).epoch || 0 } catch (e) { return 0 } })(reply)) | 0;
                        return (epoch === 0) ? (Promise.resolve(new FSharpResult$2(/* Error */ 1, [fault(concat("the sidecar\'s build reply named no session: ", reply))]))) : (Promise.resolve(new FSharpResult$2(/* Ok */ 0, [epoch])));
                    }
                    else {
                        const e_1 = matchValue_1;
                        return Promise.resolve(new FSharpResult$2(/* Error */ 1, [e_1]));
                    }
                });
            }
            else {
                const e = matchValue;
                return Promise.resolve(new FSharpResult$2(/* Error */ 1, [e]));
            }
        });
    }))));
}

/**
 * Run the session towards `cycle` for one chunk, and answer with the clock it reached and
 * whether it got there.
 * 
 * **One chunk, not a loop.** A run is a SEQUENCE of these and the sequence belongs in the update
 * function, which is where the sequencing of everything else on this protocol lives.
 * 
 * **No operation is ever cancelled in the middle.** Every one runs to completion and answers -
 * this one answers with the clock it reached and whether that was the cycle asked for. Cancelling
 * a run is deciding not to ask for the next chunk, which needs nothing of the protocol, nothing
 * of the sidecar, and nothing of the promise already running. Reaching into an operation in
 * flight would be a mechanism this protocol has nowhere else, and the whole reason a run is
 * chunked at all is so that it does not need one.
 * 
 * It is also what lets the clock be model state - `SidecarSession`, updated by each answer - so
 * that a progress bar is drawn from it like anything else.
 * 
 * The loop that used to be here reported progress through a callback and checked no cancellation.
 * Every caller passed `ignore`, so ten round trips a second bought neither of the two things
 * chunking is for.
 */
export function runChunk(epoch, cycle) {
    return PromiseBuilder__Run_212F1D4B(promise, PromiseBuilder__Delay_62FBFDE1(promise, () => (simRun(epoch, cycle, Constants_runChunkMs).then((_arg) => {
        const reply = _arg;
        const matchValue = errorIn(reply);
        if (matchValue == null) {
            const parsed = JSON.parse(reply);
            const tick = parsed.clockTick | 0;
            const finished = parsed.done;
            return Promise.resolve(new FSharpResult$2(/* Ok */ 0, [[tick, finished]]));
        }
        else {
            const e = matchValue;
            return Promise.resolve(new FSharpResult$2(/* Error */ 1, [e]));
        }
    }))));
}

